/**
 * Configurable lookback (calendar years, decimals allowed) for rolling Z-score indicators.
 * Import-free of engine to avoid circular bundles.
 */

export const Z_SCORE_INDICATORS = [
  "ev_ebitda_z",
  "div_yield_z",
  "pb_ratio",
  "pe_ratio",
  "poi_ratio",
  "ps_ratio",
  "cf_price",
];

/** Legacy ids that map to a base indicator with 5y default when no saved preference. */
export const Z_SCORE_LEGACY_5Y_KEYS = [
  "ev_ebitda_z_5y",
  "div_yield_z_5y",
  "pb_ratio_5y",
  "pe_ratio_5y",
  "poi_ratio_5y",
  "ps_ratio_5y",
  "cf_price_5y",
];

export const DEFAULT_Z_SCORE_YEARS = 2;
export const Z_SCORE_YEARS_MIN = 0.01;
export const Z_SCORE_YEARS_MAX = 50;
export const Z_SCORE_YEARS_STORAGE_KEY = "indicatorZScoreYears";

export const Z_SCORE_BASE_LABELS = {
  ev_ebitda_z: "EV/EBITDA Yield",
  div_yield_z: "Dividend Yield",
  pb_ratio: "P/B Ratio",
  pe_ratio: "P/E Ratio",
  poi_ratio: "P/Operating Income",
  ps_ratio: "P/S Ratio",
  cf_price: "Price/Cash Flow",
};

const LEGACY_TO_BASE = Object.fromEntries(
  Z_SCORE_LEGACY_5Y_KEYS.map((k) => [k, k.replace(/_5y$/, "").replace(/_z_5y$/, "_z")])
);

export function isZScoreConfigurable(key) {
  if (!key) return false;
  const base = normalizeZScoreIndicatorKey(key);
  return Z_SCORE_INDICATORS.includes(base);
}

export function normalizeZScoreIndicatorKey(key) {
  if (!key) return key;
  if (LEGACY_TO_BASE[key]) return LEGACY_TO_BASE[key];
  if (Z_SCORE_INDICATORS.includes(key)) return key;
  return key;
}

export function isLegacyZScore5yKey(key) {
  return Z_SCORE_LEGACY_5Y_KEYS.includes(key);
}

/** Parse user input; returns finite number or null. Max 2 decimal places. */
export function parseZScoreYearsInput(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (n < Z_SCORE_YEARS_MIN || n > Z_SCORE_YEARS_MAX) return null;
  const decimals = (s.split(".")[1] || "").length;
  if (decimals > 2) return null;
  return Math.round(n * 100) / 100;
}

export function formatZScoreYears(years) {
  const y = Number(years);
  if (!Number.isFinite(y)) return `${DEFAULT_Z_SCORE_YEARS}y`;
  const rounded = Math.round(y * 100) / 100;
  return `${rounded}y`;
}

export function yearsToLookbackSec(years) {
  const y = Number(years);
  if (!Number.isFinite(y) || y <= 0) return DEFAULT_Z_SCORE_YEARS * 365 * 24 * 3600;
  return y * 365 * 24 * 3600;
}

export function getZScoreIndicatorLabel(baseKey, years = DEFAULT_Z_SCORE_YEARS) {
  const base = Z_SCORE_BASE_LABELS[normalizeZScoreIndicatorKey(baseKey)] || baseKey;
  return `${base} (Z-score vs ${formatZScoreYears(years)} avg)`;
}

export function loadZScoreYearsMap() {
  try {
    const raw = localStorage.getItem(Z_SCORE_YEARS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveZScoreYearsForIndicator(indicatorKey, years, prevMap = {}) {
  const base = normalizeZScoreIndicatorKey(indicatorKey);
  const parsed = parseZScoreYearsInput(years);
  if (parsed == null || !Z_SCORE_INDICATORS.includes(base)) return prevMap;
  const next = { ...prevMap, [base]: parsed };
  try {
    localStorage.setItem(Z_SCORE_YEARS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function getZScoreYearsForIndicator(indicatorKey, map = {}) {
  const base = normalizeZScoreIndicatorKey(indicatorKey);
  if (map[base] != null) {
    const parsed = parseZScoreYearsInput(map[base]);
    if (parsed != null) return parsed;
  }
  if (isLegacyZScore5yKey(indicatorKey)) return 5;
  return DEFAULT_Z_SCORE_YEARS;
}
